import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { verifyEmailApi } from '~/api';

import { useBoolean } from '~/hooks/useBoolean';
import { loginSuccess } from '~/store/actions';
import { IBusiness } from '~/types/business';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import { PlanType } from '~/utils/payment';

interface IVerifyEmailResponse {
  token: string;
  user_business: IBusiness;
  first_name: string;
  last_name: string;
  companies: Array<IBusiness>;
  current_plan: PlanType;
  email: string;
  user_id: number;
}

export function useVerifyEmail() {
  const [loading, loadingActions] = useBoolean();
  const [msg, setMsg] = useState('');
  const { token } = useParams();
  const dispatch = useDispatch();

  async function verifyEmail() {
    if (!token) {
      toast.error('Invalid Token');
      return;
    }

    loadingActions.on();
    const promise = verifyEmailApi(token);
    const response = await apiResponseHandler<IVerifyEmailResponse>(promise);

    if (response.type === ApiResponseEnum.Success) {
      // Check if the response contains login data (same format as login API)
      if (response.data && response.data.token) {
        // Auto-login the user
        dispatch(
          loginSuccess({
            token: response.data.token,
            userBusiness: response.data.user_business,
            first_name: response.data.first_name,
            last_name: response.data.last_name,
            email: response.data.email,
            companies: response.data.companies,
            current_plan: response.data.current_plan,
            user_id: response.data.user_id,
          }),
        );

        setMsg('Email verified successfully! You are now logged in.');
      } else {
        // Fallback to old behavior if no login data
        setMsg(response.msg ?? 'Email Verified! You may proceed to login now');
      }
    } else {
      setMsg(response.error.toString() ?? 'An error occurred!');
    }
    loadingActions.off();
  }

  useEffect(() => {
    verifyEmail();
  }, [token]);

  return { msg, loading };
}
